// Tiny i18n runtime: t() with {name}-interpolation over the flat dictionaries
// in messages.ts, a React provider bound to the module-level locale store in
// locale.ts, and helpers for enum labels (labelizeT) and localized entity
// names from the i18n jsonb column (entityName).
import { createContext, useContext, useMemo, useSyncExternalStore, type ReactNode } from "react";
import { getLocale, setLocale, subscribeLocale, LOCALES, type Locale } from "./locale";
import { dictionaries, en, type MessageKey } from "./messages";
import { labelize } from "./format";

export { LOCALES, getLocale, setLocale, type Locale };
export type { MessageKey };

const warned = new Set<string>();

function warnOnce(message: string): void {
  if (warned.has(message)) return;
  warned.add(message);
  console.warn(`[i18n] ${message}`);
}

function interpolate(template: string, params?: Record<string, string | number>): string {
  if (!params) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) =>
    name in params ? String(params[name]) : m,
  );
}

/** Translate `key` in `locale`: locale dict → en → the key itself. */
export function translate(locale: Locale, key: MessageKey, params?: Record<string, string | number>): string {
  const dict: Record<string, string> = dictionaries[locale] ?? en;
  let template = dict[key];
  if (template == null) {
    warnOnce(`missing key "${key}" for locale "${locale}"`);
    template = (en as Record<string, string>)[key] ?? key;
  }
  return interpolate(template, params);
}

/**
 * Translation-aware labelize(): looks up `enum.<VALUE>` for the wire value
 * (case-insensitive) and falls back to plain labelize() for values without a
 * dictionary entry. Never used to build wire values — display only.
 */
export function labelizeLocale(locale: Locale, value: string): string {
  const key = `enum.${value.toUpperCase()}`;
  const dict: Record<string, string> = dictionaries[locale] ?? en;
  return dict[key] ?? (en as Record<string, string>)[key] ?? labelize(value);
}

export type EntityI18n = { name?: Record<string, string> | null } | null | undefined;

/**
 * Localized entity name from the `i18n` jsonb column, shape
 * `{"name": {"de": "...", "fr": "..."}}`, falling back to the base name.
 */
export function entityNameLocale(
  locale: Locale,
  entity: { name: string; i18n?: EntityI18n },
): string {
  return entity.i18n?.name?.[locale] || entity.name;
}

const LocaleContext = createContext<Locale>("en");

export function I18nProvider({ children }: { children: ReactNode }) {
  // Third argument (server snapshot) lets react-dom/server render the tree in
  // scripts/i18n-check.tsx; the app itself never server-renders.
  const locale = useSyncExternalStore(subscribeLocale, getLocale, getLocale);
  return <LocaleContext.Provider value={locale}>{children}</LocaleContext.Provider>;
}

export function useLocale(): Locale {
  return useContext(LocaleContext);
}

/**
 * Locale-bound helpers. Components must take these from the hook (not the
 * standalone functions) so they re-render when the locale changes.
 */
export function useT() {
  const locale = useContext(LocaleContext);
  return useMemo(
    () => ({
      locale,
      t: (key: MessageKey, params?: Record<string, string | number>) => translate(locale, key, params),
      labelizeT: (value: string) => labelizeLocale(locale, value),
      entityName: (entity: { name: string; i18n?: EntityI18n }) => entityNameLocale(locale, entity),
    }),
    [locale],
  );
}
